import React, { useEffect, useState } from "react";
import { api, formatDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";

const ACTION_COLORS = {
  "user.create": "#34C759", "user.delete": "#FF453A", "user.invite": "#5AC8FA",
  "election.create": "#007AFF", "election.update": "#32ADE6", "election.delete": "#FF453A",
  "candidate.create": "#34C759", "candidate.delete": "#FF453A",
  "location.create": "#34C759", "location.delete": "#FF453A", "location.bulk_import": "#5AC8FA",
  "result.submit": "#FF9F0A", "result.approve": "#34C759", "result.reject": "#FF453A",
  "audit.reset": "#AF52DE",
};

const ACTION_FILTERS = [
  { key: "all", label: "All", match: () => true },
  { key: "user.password_reset", label: "Password resets", match: (a) => a === "user.password_reset" || a === "auth.reset_password" },
  { key: "auth", label: "Auth", match: (a) => a.startsWith("auth.") || a === "user.password_reset" },
  { key: "result", label: "Results", match: (a) => a.startsWith("result.") },
  { key: "system", label: "System", match: (a) => a.startsWith("system.") },
  { key: "assignments", label: "Assignments", match: (a) => a.startsWith("assignments.") },
];

export default function AuditLog() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [resetting, setResetting] = useState(false);

  const load = () => api.get("/audit-log", { params: { limit: 500 } }).then(r => setLogs(r.data));
  useEffect(() => { load(); }, []);

  const runReset = async () => {
    setResetting(true);
    try {
      const { data } = await api.post("/audit-log/reset");
      toast.success(`Audit log reset — ${data.cleared} entries cleared`);
      setConfirmOpen(false); setConfirmText("");
      load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setResetting(false); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1400px]">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Trail</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2" data-testid="audit-title">Audit Log</h1>
        </div>
        {user?.is_owner && (
          <button onClick={() => setConfirmOpen(true)} data-testid="reset-audit-btn"
            className="px-4 py-2 border border-red-500/40 text-red-400 hover:bg-red-500/10 text-xs uppercase tracking-[0.2em] flex items-center gap-2">
            <Trash2 size={14}/> Reset for Production
          </button>
        )}
      </div>

      <div className="mt-6 flex items-center gap-2 flex-wrap" data-testid="audit-filter-bar">
        {ACTION_FILTERS.map(f => (
          <button key={f.key} onClick={()=>setFilter(f.key)} data-testid={`audit-filter-${f.key}`}
            className={`px-3 py-1.5 border text-[10px] uppercase tracking-[0.2em] ${filter===f.key?"bg-white text-black border-white":"border-white/15 text-zinc-400 hover:text-white hover:border-white/40"}`}>{f.label}</button>
        ))}
        <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Filter by email…" data-testid="audit-search"
          className="ml-auto bg-transparent border border-white/15 px-3 py-1.5 text-sm outline-none focus:border-white w-64"/>
      </div>

      <div className="mt-4 border border-white/10 data-table">
        <div className="grid grid-cols-12 px-4 py-3 border-b border-white/10 text-[10px] uppercase tracking-[0.3em] text-zinc-500">
          <div className="col-span-2">Timestamp</div>
          <div className="col-span-3">Actor</div>
          <div className="col-span-3">Action</div>
          <div className="col-span-4">Details</div>
        </div>
        <div className="divide-y divide-white/5 max-h-[70vh] overflow-y-auto">
          {logs
            .filter(l => (ACTION_FILTERS.find(f => f.key === filter)?.match(l.action) ?? true))
            .filter(l => !q.trim() || (l.actor_email||"").toLowerCase().includes(q.trim().toLowerCase()) || JSON.stringify(l.details||{}).toLowerCase().includes(q.trim().toLowerCase()))
            .map(l => (
            <div key={l.id} className={`grid grid-cols-12 px-4 py-2.5 items-center text-sm hover:bg-white/[0.02] ${l.action==="user.password_reset"?"bg-amber-500/[0.04]":""}`}>
              <div className="col-span-2 text-xs text-zinc-500 tabular">{(l.timestamp||"").replace("T"," ").slice(0,19)}</div>
              <div className="col-span-3 truncate">
                <div className="truncate">{l.actor_email}</div>
                <div className="text-[10px] uppercase text-zinc-500 tracking-wider">{l.actor_role === "observer" ? "Reviewer" : l.actor_role}</div>
              </div>
              <div className="col-span-3 flex items-center gap-2">
                <span className="w-1.5 h-1.5" style={{ background: ACTION_COLORS[l.action] || "#71717a" }}/>
                <span className="uppercase tracking-wider text-xs">{l.action}</span>
              </div>
              <div className="col-span-4 text-xs text-zinc-400 truncate">
                {l.action === "user.password_reset" && l.details?.target_email
                  ? <span><span className="text-amber-300">reset →</span> <span className="text-white">{l.details.target_email}</span></span>
                  : Object.entries(l.details || {}).map(([k,v])=>`${k}: ${JSON.stringify(v)}`).join(" · ")}
              </div>
            </div>
          ))}
          {!logs.length && <div className="p-6 text-zinc-500 text-sm">No activity yet.</div>}
        </div>
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={()=>setConfirmOpen(false)} data-testid="reset-confirm-modal">
          <div onClick={(e)=>e.stopPropagation()} className="bg-white border border-red-500/30 max-w-lg w-full p-6">
            <div className="flex items-start gap-3">
              <AlertTriangle size={28} className="text-red-400 shrink-0 mt-1"/>
              <div>
                <div className="font-display text-2xl">Reset audit log for production</div>
                <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
                  This deletes <span className="tabular text-white">{logs.length}</span> audit entries. Use it only right before you go live — every submit, approve, reject, edit and login-related admin action was recorded here and will be permanently lost.
                </p>
                <p className="text-xs text-zinc-500 mt-3">Type <span className="text-white tabular font-mono">RESET</span> to confirm.</p>
                <input value={confirmText} onChange={(e)=>setConfirmText(e.target.value)} autoFocus
                  data-testid="reset-confirm-input"
                  className="w-full mt-2 bg-transparent border-b border-white/20 py-2 outline-none focus:border-red-400 font-mono text-lg tracking-wider"/>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={()=>{ setConfirmOpen(false); setConfirmText(""); }} className="px-4 py-2 border border-white/20 text-xs uppercase tracking-[0.2em] hover:bg-white/10">Cancel</button>
              <button onClick={runReset} disabled={confirmText.trim() !== "RESET" || resetting}
                data-testid="reset-confirm-btn"
                className="px-4 py-2 bg-red-500 text-black text-xs uppercase tracking-[0.2em] font-semibold hover:bg-red-400 disabled:opacity-40 flex items-center gap-2">
                {resetting ? <Loader2 size={13} className="animate-spin"/> : <Trash2 size={13}/>}
                {resetting ? "Resetting" : "Reset audit log"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
